import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { isDisabledByPos } from 'src/utils/helpers';
import AddButton from 'src/view/components/Controls/AddButton/AddButton';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import SectionListRow from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/components/SectionListRow/SectionListRow';
import branchEditStore from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/store/branchEditStore';
import { isFranchiseDisableByPos } from '~/helpers';
import { appStore } from '~/mobx/appStore';

const SectionsList: FC = observer((): JSX.Element => {
  const { sectionsAndTables: sectionsRestrictionAccess } = integrationsStore.posRestrictions || {};

  const areSectionsDisabledByPos = useMemo(() => {
    if (isFranchiseDisableByPos(branchEditStore.editableBranch.id, branchEditStore.editableBranch.created_in)) {
      return false;
    }

    return isDisabledByPos(sectionsRestrictionAccess);
  }, [branchEditStore.editableBranch.id, sectionsRestrictionAccess, appStore.isAddonsAvailable.franchise]);

  const showDineInSectionModal = () => {
    modalStore.openDineInSectionModal({
      restrictionAccess: sectionsRestrictionAccess,
      disabledForEditing: areSectionsDisabledByPos,
      onSave: branchEditStore.updateBranchSections,
    });
  };

  if (!branchEditStore.sections?.length) {
    return (
      <div className="d-flex align-items-center justify-content-center full-height">
        <div className="d-flex flex-column align-items-center">
          <p className="family-medium text-size-16 color-gray2">{localeStore.t('no_sections_yet')}</p>
          <IntegrationManager setting={sectionsRestrictionAccess} withTooltip tooltipOptions={{ show: areSectionsDisabledByPos }}>
            <AddButton title="create_new_section_button" className="mt-2 mx-auto" onAdd={showDineInSectionModal} />
          </IntegrationManager>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column full-height full-width">
      <div className="d-flex pb-4 pr-3">
        <div className="family-medium text-size-12 text-uppercase letter-spacing-12 color-gray2 w-60">{localeStore.t('section')}</div>
        <div className="family-medium text-size-12 text-uppercase letter-spacing-12 color-gray2 w-40">{localeStore.t('number_of_tables')}</div>
      </div>

      <ScrollableBlock showScrollbar scrollbarSize="small" className="overflow-y-scroll">
        {branchEditStore.sections.map((section) => (
          <SectionListRow
            key={section.id}
            sectionId={section.id}
            sectionName={section.name}
            sectionTables={section.tables}
            restrictionAccess={sectionsRestrictionAccess}
            disabledForEditing={areSectionsDisabledByPos}
          />
        ))}

        <IntegrationManager setting={sectionsRestrictionAccess} withTooltip tooltipOptions={{ show: areSectionsDisabledByPos }}>
          <AddButton title="create_new_section_button" className="mt-2 mx-auto" onAdd={showDineInSectionModal} />
        </IntegrationManager>
      </ScrollableBlock>
    </div>
  );
});

export default SectionsList;
